/*
 * -----------------------------------------------------------------------
 *  <copyright file="gauge-chart.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */
import React, { useState, useEffect, useRef, useContext } from 'react';
import Highcharts from 'highcharts/esm/highcharts.js';
import 'highcharts/highcharts-3d';
import 'highcharts/modules/exporting';
import 'highcharts/modules/offline-exporting';
import HighchartsReact from 'highcharts-react-official';
import { getChartEvents } from '@components-clean/core/charts/shared/utils';

import {
    HC_COLORS_STANDARD,
    HC_COLORS_MUTED,
    HC_COLORS_SAND_SIGNIKA,
    OpraColours,
    OpraVerticalAlign,
    IOpraChartKey,
} from 'interfaces/opra-chart.interface';
import Loading from 'views/components/core/loading';
import { PanelFrameContext } from 'views/components/core/panel-frame';

export interface IOpraDonutSeries {
    name: string;
    colour: OpraColours;
    segmentSize: number;
}

export interface IOpraDonutChart {
    title?: string;
    entityType: string;
    data: IOpraDonutSeries[];
    threeDee?: boolean;
    onClick?: any;
    getRef: Function | null;
    secondaryKey?: IOpraChartKey;
    showLegend?: boolean;
    noDataMessage?: string;
    showDataLabels?: boolean;
    hidePlotTitle?: boolean;
    displayFullScreenAndPrintTitle?: boolean;
}

const styleHeight = { height: '350px' };

const DonutChart = (chartProps: IOpraDonutChart) => {
    const {
        title,
        entityType,
        data,
        threeDee,
        onClick,
        getRef,
        secondaryKey,
        showLegend,
        noDataMessage,
        showDataLabels,
        hidePlotTitle,
        displayFullScreenAndPrintTitle = false,
    } = chartProps;
    const [options, setOptions] = useState<HighchartsReact.Props | null>(null);
    const [hasData, setHasData] = useState(true);

    const charts = useRef(null);
    const height = useContext(PanelFrameContext);
    const [style, setStyle] = useState({
        style: {
            height: '350px',
        },
    });

    useEffect(() => {
        if (height?.height && height?.height !== style.style.height)
            setStyle({
                style: {
                    height: height?.height,
                },
            });
    }, [height?.height]);

    useEffect(() => {
        if (getRef && charts) getRef(charts?.current);
    }, [charts.current, options]);

    useEffect(() => {
        const tTip: string = `{point.y} {series.name} of ${data.reduce(
            (total: number, plot: IOpraDonutSeries): number => total + plot.segmentSize,
            0,
        )}`;
        setHasData(data.reduce((tot: number, seg) => tot + (seg.segmentSize ?? 0), 0) > 0);
        setOptions({
            colors: HC_COLORS_SAND_SIGNIKA,
            credits: {
                enabled: false,
            },
            chart: {
                type: 'pie',
                options3d: threeDee
                    ? {
                          enabled: true,
                          alpha: 45,
                      }
                    : undefined,
                events: displayFullScreenAndPrintTitle
                    ? getChartEvents(false, false, title, undefined, {
                          fullScreenOptions: {
                              title: { text: title, style: { fontWeight: '600' } },
                          },
                          printOptions: {
                              title: { text: title, style: { fontWeight: '500' } },
                          },
                      })
                    : undefined,
            },
            exporting: {
                enabled: !getRef,
                chartOptions: { title: { text: title, style: { fontWeight: 'semi-bold' } } },
                fallbackToExportServer: false,
                buttons: {
                    contextButton: {
                        enabled: false,
                    },
                },
            },
            title: hidePlotTitle ? {} : { text: title },
            subtitle:
                secondaryKey === undefined
                    ? undefined
                    : {
                          text: secondaryKey.text,
                          useHTML: true,
                          verticalAlign:
                              secondaryKey.position === OpraVerticalAlign.top
                                  ? 'top'
                                  : secondaryKey.position === OpraVerticalAlign.middle
                                    ? 'middle'
                                    : 'bottom',
                          style: { display: (showLegend ?? true) ? undefined : 'none' },
                      },
            legend: {
                enabled: showLegend ?? true,

                itemStyle: {
                    color: HC_COLORS_STANDARD[OpraColours.DarkGrey],
                    cursor: 'pointer',
                    fontWeight: 'normal',
                    fontSize: '12px',
                    textOverflow: 'ellipsis',
                },
                layout: 'vertical',
                align: 'right',
                maxHeight: 200,
                verticalAlign: 'middle',
                width: 200,
            },
            tooltip: {
                headerFormat: '<span><b>{point.key}:</b> {point.percentage:.0f}%</span><br/>',
                pointFormat: tTip,
            },
            plotOptions: {
                pie: {
                    dataLabels: {
                        enabled: showDataLabels,
                        distance: -30,
                        style: {
                            fontSize: '11px',
                        },
                        formatter() {
                            const self: any = this;
                            return self.y > 0 ? self.y : null;
                        },
                    },
                    showInLegend: true,
                    innerSize: 100,
                    depth: 45,
                    point: onClick ? { events: { click: onClick } } : undefined,
                    cursor: onClick ? 'pointer' : undefined,
                },
            },
            series: [
                {
                    name: entityType || '',
                    data: data.map((plot: IOpraDonutSeries) => ({
                        name: plot.name,
                        y: plot.segmentSize,
                        color:
                            plot.colour === undefined
                                ? undefined
                                : threeDee
                                  ? HC_COLORS_STANDARD[plot.colour]
                                  : HC_COLORS_MUTED[plot.colour],
                    })),
                },
            ],
        });
    }, [chartProps]);
    if (options === null) return <Loading />;
    return hasData || !noDataMessage ? (
        <HighchartsReact
            ref={charts}
            highcharts={Highcharts}
            constructorType="chart"
            options={options}
            containerProps={style}
        />
    ) : (
        <div ref={charts} style={styleHeight}>
            {noDataMessage}
        </div>
    );
};

export default DonutChart;
